import FocusTrap from "focus-trap-react";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import type { FC, ReactNode, FocusEventHandler } from "react";
import { CSSTransition } from "react-transition-group";
import { ConditionalWrapper } from "../ConditionalWrapper";
import { NectaryIcon } from "../Icon";
import { useLocation } from "../Router/state";
import { NavIcon, type NavIcons } from "./NavIcon/NavIcon";
import { NavigationBarPortal } from "./NavigationBarPanel/NavigationBarPortal";
import {
  Button,
  ItemContainer,
  IconButton,
  IconWrapper,
  ItemText,
  ItemButtonContainer,
  SubMenuPanel,
  StyledSubMenuHeader,
} from "./SidebarItemElements";
import { SubMenu } from "./SubMenu";
import { useNavigationHoverContext } from "./contexts/NavigationHoverContext";
import { useSidebarState, useSidebarDispatch } from "./state";
import { animationDuration } from "./theme";
import { isItemActive } from "./utils";
import "@nectary/components/tooltip";

const usePrevious = <T,>(value: T): T | null => {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

type SidebarItemProps = {
  to: string;
  text: string;
  icon: NavIcons;
  id: string;
  onHover?: () => void;
  children?: ReactNode;
  isFlex?: boolean;
  path: string;
  subPaths: string[];
};

export const SidebarItem: FC<SidebarItemProps> = ({
  text,
  icon,
  id,
  to,
  onHover,
  children,
  subPaths,
  path,
}) => {
  const location = useLocation();

  const isActive = useMemo(
    () => isItemActive(path, location.pathname, subPaths),
    [path, subPaths, location.pathname]
  );
  const wasActive = usePrevious(isActive);
  const sidebarState = useSidebarState();
  const { hoveredElementKey, setHoveredElementKey } =
    useNavigationHoverContext();
  const triggerRef = useRef<HTMLDivElement>(null);
  const transitionRef = useRef(null);
  const [isFocusTrapActive, setFocusTrapActive] = useState(false);
  const [isFocusTrapDisabled, setFocusTrapDisabled] = useState<boolean>(false);
  const hasSubNavigation = !!children;
  const sidebarDispatch = useSidebarDispatch();
  const hover = id === hoveredElementKey;
  const handleOnEntered = useCallback(() => setFocusTrapActive(true), []);
  const handleOnExit = useCallback(() => setFocusTrapActive(false), []);
  const handleMouseEnter = useCallback(() => {
    hasSubNavigation && setFocusTrapDisabled(true);
    setHoveredElementKey(hasSubNavigation ? id : null);
  }, [id, hasSubNavigation, setHoveredElementKey]);

  const focusTrapOptions = useMemo<FocusTrap.Props["focusTrapOptions"]>(
    () => ({
      allowOutsideClick: true,
      setReturnFocus() {
        return triggerRef.current ?? false;
      },
    }),
    []
  );
  const handleFocus = useCallback<FocusEventHandler>(
    (event) => {
      if (hover && event.target === triggerRef.current) {
        setHoveredElementKey(null);
      }
    },
    [hover, setHoveredElementKey]
  );

  useEffect(() => {
    if (!wasActive && isActive) {
      sidebarDispatch({
        type: "SUBMENU_CLICK",
        payload: { subMenuId: id },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return (
    <>
      <ConditionalWrapper
        shouldWrap={sidebarState.isCollapsed}
        wrapper={(children) => (
          <sinch-tooltip text={text} type="fast" orientation={"right"}>
            {children}
          </sinch-tooltip>
        )}
      >
        <ItemContainer
          isCollapsed={sidebarState.isCollapsed}
          id={id}
          isFlex
          onMouseEnter={() => handleMouseEnter()}
          onFocus={handleFocus}
          ref={triggerRef}
        >
          <ItemButtonContainer isActive={isActive}>
            <Button onMouseOver={onHover} to={to} $isActive={isActive}>
              {/* Prepare Icon wrapper for inbox-item mfe mounting */}
              <IconWrapper>
                <NavIcon name={icon} />
              </IconWrapper>

              {!sidebarState.isCollapsed && (
                <ItemText isActive={isActive}>{text}</ItemText>
              )}
            </Button>
            {children && !sidebarState.isCollapsed && (
              <IconButton
                onClick={() =>
                  sidebarDispatch({
                    type: "SUBMENU_ARROW_CLICKED",
                    payload: { subMenuId: id },
                  })
                }
              >
                {sidebarState.openedSubMenu === id ? (
                  <NectaryIcon name="keyboard-arrow-up" />
                ) : (
                  <NectaryIcon name="keyboard-arrow-down" />
                )}
              </IconButton>
            )}
          </ItemButtonContainer>
        </ItemContainer>
      </ConditionalWrapper>
      {children && (
        <>
          <NavigationBarPortal>
            <CSSTransition
              in={hover}
              onEntered={handleOnEntered}
              onExit={handleOnExit}
              nodeRef={transitionRef}
              timeout={animationDuration.slow}
            >
              <FocusTrap
                active={isFocusTrapActive && !isFocusTrapDisabled}
                focusTrapOptions={focusTrapOptions || {}}
              >
                <SubMenuPanel ref={transitionRef}>
                  <StyledSubMenuHeader>{text}</StyledSubMenuHeader>
                  {children}
                </SubMenuPanel>
              </FocusTrap>
            </CSSTransition>
          </NavigationBarPortal>
          <SubMenu id={id}>{children}</SubMenu>
        </>
      )}
    </>
  );
};
